// CFG.I18N.A2: Payment-by-region display.
//
// Regional currency selection + formatting for marketing prices in the
// signup funnel and billing portal. Display only — nothing here touches
// actual charges. The payment integration (CFG.BILL.A3+) will own real
// money. This module's job is to make the numbers on screen match the
// tenant's region so an Indian customer doesn't see "$49/seat/month" and
// wonder whether they'll actually be charged dollars.
//
// Detection priority:
//   1. i18next locale — but ONLY as an affirmative INR signal. `en-IN`
//      forces INR (the user / product has explicitly chosen Indian
//      English, honour it over any timezone). A non-regional locale
//      (bare `en`, `en-US`, etc.) is NOT treated as "not India" — it
//      falls through to the timezone check, because today's default
//      locale is `en` and every Indian tenant is still on it.
//   2. Timezone — either browser-side (`Intl.DateTimeFormat`) or
//      tenant-stored (`agentConfigOverride.timezone`). The caller
//      decides which to pass; this function just checks the value.
//      `Asia/Kolkata` → INR.
//   3. Browser locale (`navigator.language`) — the user's OS/browser
//      language preference. Distinct from i18next locale because a
//      user whose browser is `en-IN` may still be on the app's default
//      `en` locale. Catches India-based users whose timezone is masked
//      by VPN, corporate proxy, or travel.
//   4. USD — safe default. Anything we don't recognise, and anything
//      null/undefined, falls here.
//
// INR is the only non-USD currency this sprint. When the next currency
// ships (likely EUR for EU tenants), the decision tree grows — keep
// `detectCurrency` the single place that maps signals to codes so the
// call sites don't sprout if/else chains.

export type Currency = 'INR' | 'USD';

/**
 * The subscription tiers that have a published marketing price. Other
 * canonical tiers stay silent in the billing UI:
 *   - INDIVIDUAL — solo plan, billed via signup funnel CTAs
 *   - ENTERPRISE — contract tier (custom pricing, no self-serve)
 *   - PLATFORM_PARTNER — partners are billed via partner_accounts agreement
 *
 * Callers must narrow via `isPriceableTier` before formatting.
 */
export type PriceableTier = 'STARTER' | 'GROWTH';

/**
 * Canonical marketing prices, per seat per month, in whole currency
 * units. Source of truth: CFG.I18N.A2 spec. Keep in sync with the
 * pricing page and any sales collateral — changing a number here
 * changes what every caller displays.
 *
 * INR values are NOT a mechanical FX conversion of the USD values;
 * they are the India-list prices negotiated for the local market.
 */
export const TIER_PRICES: Record<PriceableTier, Record<Currency, number>> = {
  STARTER: { USD: 49, INR: 4099 },
  GROWTH:  { USD: 79, INR: 6599 },
};

/**
 * True when a tier string corresponds to a publicly priced plan. Used to
 * gate the Price row in the billing portal — STARTER/GROWTH partners see
 * a price; INDIVIDUAL/ENTERPRISE/PLATFORM_PARTNER render the plan name
 * but no numeric price row.
 */
export function isPriceableTier(tier: string | null | undefined): tier is PriceableTier {
  return tier === 'STARTER' || tier === 'GROWTH';
}

/**
 * Decide the display currency from the available signals.
 *
 * Locale is checked first because it's an explicit choice (the user or
 * an org admin picked `en-IN` in the language switcher). Timezone is the
 * fallback for everyone on the default `en` locale — if the tenant
 * configured `Asia/Kolkata` at onboarding, show them INR.
 *
 * `browserLocale` (`navigator.language`) is checked after timezone as a
 * defense-in-depth signal for users whose browser timezone is masked by
 * VPN/proxy/travel but whose OS language is still `en-IN`. It is NOT
 * the same as the i18next `locale` — the app defaults to `'en'` even
 * for Indian users, so the framework locale is rarely `'en-IN'` unless
 * explicitly switched.
 *
 * Case-insensitive locale match so `EN-IN`, `en-IN`, and `en-in` all
 * resolve the same way. Timezone is compared against both the canonical
 * IANA ID (`Asia/Kolkata`) and the legacy alias (`Asia/Calcutta`,
 * renamed in tzdata 2001 but still reported by some older browsers).
 * India has one timezone (IST, UTC+5:30) so there are no other
 * variants to worry about.
 */
export function detectCurrency(opts: {
  locale?: string | null;
  timezone?: string | null;
  browserLocale?: string | null;
}): Currency {
  const locale = opts.locale?.toLowerCase();
  if (locale === 'en-in') return 'INR';
  // India has one timezone (IST, UTC+5:30). The canonical IANA name is
  // Asia/Kolkata, but older systems and some browsers still report the
  // legacy alias Asia/Calcutta (renamed in tzdata 2001).
  const tz = opts.timezone;
  if (tz === 'Asia/Kolkata' || tz === 'Asia/Calcutta') return 'INR';
  // navigator.language can be any BCP-47 tag with an `-IN` region subtag:
  // en-IN, hi-IN, ta-IN, bn-IN, mr-IN, etc. All unambiguously indicate
  // India, so match the region subtag rather than requiring a specific
  // primary language.
  const bl = opts.browserLocale?.toLowerCase();
  if (bl?.endsWith('-in')) return 'INR';
  return 'USD';
}

/**
 * Format a whole-unit price as `"$49/seat/month"` or `"₹4,099/seat/month"`.
 *
 * Cents are dropped (`maximumFractionDigits: 0`) — marketing prices are
 * whole numbers and rendering `$49.00` looks unfinished. If a future
 * tier needs fractional prices, add a variant rather than loosening
 * this default, so the main call sites stay predictable.
 *
 * The `/seat/month` suffix is baked into the output string deliberately:
 * every caller in the app today (signup tier badge, billing portal
 * Price row) wants it, and keeping the unit with the number prevents
 * translators/designers from accidentally separating them and losing
 * the relationship.
 */
export function formatPrice(amount: number, currency: Currency): string {
  // Pair each currency with the locale whose grouping rules match how
  // that currency is normally written. `en-IN` gives Indian lakh/crore
  // grouping for larger amounts (e.g. 1,00,000 instead of 100,000);
  // today's INR prices are all under a lakh so the visible difference
  // only matters if/when prices grow, but it's the right default.
  const formattingLocale = currency === 'INR' ? 'en-IN' : 'en-US';

  const formatted = new Intl.NumberFormat(formattingLocale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);

  return `${formatted}/seat/month`;
}

/**
 * Convenience for the common case: given a tier and a chosen currency,
 * look up the price and format it. Call this from render paths; call
 * `formatPrice` directly only when you have a custom amount.
 */
export function formatTierPrice(tier: PriceableTier, currency: Currency): string {
  return formatPrice(TIER_PRICES[tier][currency], currency);
}
